/**
 * Libreria file delle newsletter: sfoglia i file già caricati, ne mostra l'anteprima e
 * permette di caricarne di nuovi, così lo stesso materiale non viene ricaricato ogni volta.
 *
 * Usata in due punti:
 *  - editor HTML, per incorporare un'immagine nel corpo (mode="image");
 *  - riquadro allegati, per allegare qualsiasi file (mode="all").
 *
 * La lista è paginata lato server: i file possono essere migliaia e non vanno mai
 * caricati tutti insieme.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  IconButton,
  Pagination,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';
//--------------------------------------------------------
import { DefaultContainedButton, DefaultOutlinedButton } from '../../../../utils/Buttons';
import { CustomTextField } from '../../../../utils/StyledInputs';
import { HttpErrorHandler } from '../../../../utils/ErrorHandler';
import { usePopup } from '../../../../context/PopupContext';
import { mediaApi } from '../../../../services/newsletter/mediaApi';

const SEARCH_DEBOUNCE_MS = 400;

/** Estensioni accettate: immagini per l'editor, anche documenti per gli allegati. */
const ACCEPT_IMAGE = 'image/jpeg,image/png,image/gif,image/webp';
const ACCEPT_ALL = `${ACCEPT_IMAGE},application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx`;

/** Dimensione massima lato BE (10 MB): si intercetta qui per non attendere l'upload a vuoto. */
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

const fmtSize = (bytes) => {
  const n = Number(bytes ?? 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

/** Riquadro di un file: anteprima per le immagini, icona con estensione per i documenti. */
function MediaCard({ media, selected, onSelect, onPreview }) {
  return (
    <Box
      onClick={() => onSelect(media)}
      sx={{
        position: 'relative',
        border: `2px solid ${selected ? '#1976d2' : '#e0e0e0'}`,
        borderRadius: 1,
        overflow: 'hidden',
        cursor: 'pointer',
        backgroundColor: '#fff',
        '&:hover': { borderColor: selected ? '#1976d2' : '#9e9e9e' },
      }}
    >
      <Box
        sx={{
          height: 110,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f4f6f8',
          overflow: 'hidden',
        }}
      >
        {media.is_image ? (
          <Box
            component="img"
            src={media.url}
            alt={media.file_name}
            loading="lazy"
            sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        ) : (
          <Stack alignItems="center" spacing={0.5}>
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 40, color: '#919eab' }} />
            <Typography variant="caption" sx={{ color: '#637381', textTransform: 'uppercase' }}>
              {(media.file_name ?? '').split('.').pop()}
            </Typography>
          </Stack>
        )}
      </Box>

      <Tooltip title={media.file_name} arrow>
        <Box sx={{ p: 1 }}>
          <Typography variant="caption" noWrap sx={{ display: 'block', fontWeight: 600 }}>
            {media.file_name}
          </Typography>
          <Typography variant="caption" sx={{ color: '#919eab' }}>
            {fmtSize(media.size)}
          </Typography>
        </Box>
      </Tooltip>

      {selected && (
        <CheckCircleIcon
          sx={{ position: 'absolute', top: 6, right: 6, color: '#1976d2', backgroundColor: '#fff', borderRadius: '50%' }}
        />
      )}

      {/* Ingrandimento: le miniature sono piccole e non bastano a riconoscere un file.
          stopPropagation, altrimenti il clic selezionerebbe anche la card. */}
      <Tooltip title={media.is_image ? 'Ingrandisci' : 'Apri in una nuova scheda'} arrow>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onPreview(media);
          }}
          sx={{
            position: 'absolute',
            top: 4,
            left: 4,
            backgroundColor: 'rgba(255,255,255,0.9)',
            '&:hover': { backgroundColor: '#fff' },
          }}
        >
          <ZoomInIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
MediaCard.propTypes = {
  media: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired,
};

/**
 * @param {{
 *   open: boolean,
 *   mode?: 'image'|'all',
 *   entityId?: string|null,
 *   onClose: () => void,
 *   onSelectMedia: (media: object) => void,
 * }} props
 */
export function MediaLibraryDialog({ open, mode = 'all', entityId, onClose, onSelectMedia }) {
  const { showPopup, setPopUpText } = usePopup();

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [search, setSearch] = useState('');
  const searchTimerRef = useRef(null);
  const inputRef = useRef(null);

  // File in anteprima ingrandita (solo immagini: i documenti si aprono in una scheda nuova).
  const [preview, setPreview] = useState(null);

  const handlePreview = (media) => {
    if (media.is_image) {
      setPreview(media);
      return;
    }
    window.open(media.url, '_blank', 'noopener,noreferrer');
  };

  const load = useCallback(
    (pageNumber, term) => {
      setLoading(true);
      mediaApi
        .list({
          page: pageNumber,
          ...(mode === 'image' ? { type: 'image' } : {}),
          ...(term ? { q: term } : {}),
        })
        .then((res) => {
          const d = res.data ?? {};
          setItems(d.data ?? []);
          setLastPage(d.last_page ?? 1);
          setTotal(d.total ?? 0);
        })
        .catch((e) => {
          HttpErrorHandler(e, setPopUpText);
          showPopup(false);
          setItems([]);
        })
        .finally(() => setLoading(false));
    },
    [mode, showPopup, setPopUpText]
  );

  // Ricarica all'apertura e ad ogni cambio di pagina o ricerca.
  useEffect(() => {
    if (open) load(page, search);
  }, [open, page, search, load]);

  // Ogni apertura riparte pulita.
  useEffect(() => {
    if (open) {
      setSelected(null);
      setSearchQuery('');
      setSearch('');
      setPage(1);
    }
  }, [open]);

  useEffect(() => () => clearTimeout(searchTimerRef.current), []);

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearchQuery(value);

    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(1);
      setSearch(value.trim());
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // permette di ricaricare lo stesso file dopo un errore
    if (!file) return;

    if (!entityId) {
      showPopup(false, "Seleziona prima l'entità mittente");
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      showPopup(false, 'Il file supera i 10 MB');
      return;
    }

    setUploading(true);
    mediaApi
      .upload(entityId, file)
      .then((res) => {
        const media = res.data;
        // 200 invece di 201: il file era già in libreria, non è stato duplicato.
        showPopup(true, res.status === 200 ? 'File già presente in libreria: selezionato' : 'File caricato');
        setSelected(media);
        setPage(1);
        setSearch('');
        setSearchQuery('');
        load(1, '');
      })
      .catch((e) => {
        HttpErrorHandler(e, setPopUpText);
        showPopup(false);
      })
      .finally(() => setUploading(false));
  };

  const handleConfirm = () => {
    if (!selected) return;
    onSelectMedia(selected);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{mode === 'image' ? 'Scegli immagine' : 'Scegli file'}</DialogTitle>

      <DialogContent dividers>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <CustomTextField
            size="small"
            placeholder="Cerca per nome file"
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#637381' }} />
                </InputAdornment>
              ),
            }}
            customStyles={{ maxWidth: 360 }}
          />

          <Stack direction="row" spacing={1} alignItems="center">
            <DefaultOutlinedButton
              startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
              disabled={uploading || !entityId}
              onClick={() => inputRef.current?.click()}
            >
              {uploading ? 'Caricamento…' : 'Carica nuovo'}
            </DefaultOutlinedButton>
            <input
              ref={inputRef}
              type="file"
              hidden
              accept={mode === 'image' ? ACCEPT_IMAGE : ACCEPT_ALL}
              onChange={handleUpload}
            />
          </Stack>
        </Stack>

        {/* La scansione antivirus rende l'upload lento: va detto, altrimenti sembra bloccato. */}
        {uploading && (
          <Typography variant="caption" sx={{ display: 'block', mb: 2, color: '#637381' }}>
            Il file viene controllato prima di essere reso disponibile: l&apos;operazione richiede qualche secondo.
          </Typography>
        )}

        {!entityId && (
          <Typography variant="caption" sx={{ display: 'block', mb: 2, color: '#b76e00' }}>
            Seleziona l&apos;entità mittente per poter caricare nuovi file.
          </Typography>
        )}

        {/* eslint-disable-next-line no-nested-ternary */}
        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : items.length === 0 ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <Typography variant="body2" sx={{ color: '#637381' }}>
              {search ? `Nessun file trovato per "${search}"` : 'Nessun file in libreria: caricane uno nuovo.'}
            </Typography>
          </Stack>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
            }}
          >
            {items.map((media) => (
              <MediaCard
                key={media.id}
                media={media}
                selected={selected?.id === media.id}
                onSelect={setSelected}
                onPreview={handlePreview}
              />
            ))}
          </Box>
        )}

        {lastPage > 1 && (
          <Stack alignItems="center" sx={{ mt: 2 }}>
            <Pagination count={lastPage} page={page} onChange={(_e, p) => setPage(p)} size="small" />
            <Typography variant="caption" sx={{ color: '#919eab', mt: 0.5 }}>
              {total} file disponibili
            </Typography>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <DefaultOutlinedButton onClick={onClose}>Annulla</DefaultOutlinedButton>
        <DefaultContainedButton onClick={handleConfirm} disabled={!selected}>
          {mode === 'image' ? 'Inserisci immagine' : 'Allega file'}
        </DefaultContainedButton>
      </DialogActions>

      {/* Anteprima ingrandita: sovrapposta alla libreria, che resta aperta sotto. */}
      <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} maxWidth="lg">
        <DialogTitle sx={{ pr: 6 }}>
          {preview?.file_name}
          <Typography variant="caption" sx={{ display: 'block', color: '#919eab' }}>
            {fmtSize(preview?.size)}
          </Typography>
          <IconButton
            onClick={() => setPreview(null)}
            sx={{ position: 'absolute', top: 12, right: 12 }}
            aria-label="Chiudi anteprima"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box
            component="img"
            src={preview?.url}
            alt={preview?.file_name}
            sx={{ display: 'block', maxWidth: '100%', maxHeight: '70vh', margin: '0 auto' }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <DefaultOutlinedButton onClick={() => setPreview(null)}>Chiudi</DefaultOutlinedButton>
          {/* Dall'anteprima si può scegliere direttamente il file, senza tornare alla griglia. */}
          <DefaultContainedButton
            onClick={() => {
              setSelected(preview);
              setPreview(null);
            }}
          >
            Seleziona questo file
          </DefaultContainedButton>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

MediaLibraryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['image', 'all']),
  entityId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSelectMedia: PropTypes.func.isRequired,
};

export default MediaLibraryDialog;
